import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useI18n } from "@/i18n";
import { brandAssets } from "@/data/brand";

const NavItem = ({ to, label, testid, onClick }) => (
  <NavLink
    to={to}
    onClick={onClick}
    data-testid={testid}
    className={({ isActive }) =>
      `link-underline text-[13px] font-semibold uppercase tracking-[0.16em] whitespace-nowrap transition-colors duration-200 ${
        isActive ? "text-white" : "text-zinc-400 hover:text-white"
      }`
    }
    end
  >
    {label}
  </NavLink>
);

export default function Navbar() {
  const { t, lang, toggle } = useI18n();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const items = [
    { to: "/", label: t("nav.home"), tid: "nav-home" },
    { to: "/about", label: t("nav.about"), tid: "nav-about" },
    { to: "/services", label: t("nav.services"), tid: "nav-services" },
    { to: "/vehicles", label: t("nav.vehicles"), tid: "nav-vehicles" },
    { to: "/partners", label: t("nav.partners"), tid: "nav-partners" },
    { to: "/process", label: t("nav.process"), tid: "nav-process" },
    { to: "/faq", label: t("nav.faq"), tid: "nav-faq" },
    { to: "/contact", label: t("nav.contact"), tid: "nav-contact" },
  ];

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-300 ${
        scrolled
          ? "bg-black/85 backdrop-blur-xl border-b border-white/[0.06]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <a href="#main" className="skip-link">Skip to content</a>
      <div className="container-mt flex items-center justify-between h-20 md:h-24">
        <Link to="/" data-testid="nav-logo" aria-label="MAD TURBO — Home" className="flex items-center shrink-0 mr-8 lg:mr-12">
          <img
            src={brandAssets.logoWordmark}
            alt="MAD TURBO"
            className="h-10 sm:h-11 md:h-12 lg:h-14 w-auto"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8" aria-label="Primary">
          {items.map((it) => (
            <NavItem key={it.to} to={it.to} label={it.label} testid={it.tid} />
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            aria-label={`Language: ${lang.toUpperCase()}. Switch language`}
            className="text-[11px] font-mono uppercase tracking-[0.25em] text-zinc-400 hover:text-brand-lime border border-zinc-800 hover:border-brand-lime/60 rounded-full px-3 py-1.5 transition-colors duration-200"
          >
            <span className={lang === "lt" ? "text-brand-lime" : ""}>LT</span>
            <span className="mx-1 text-zinc-600">/</span>
            <span className={lang === "en" ? "text-brand-lime" : ""}>EN</span>
          </button>
          <button
            className="lg:hidden text-white p-2 -mr-2"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/5 bg-black/95 backdrop-blur-xl">
          <div className="container-mt py-8 flex flex-col gap-6">
            {items.map((it) => (
              <NavItem key={it.to} to={it.to} label={it.label} testid={`m-${it.tid}`} onClick={() => setOpen(false)} />
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
